import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { blogTeaserPosts } from "@/data/blog-posts/teaser";
import { blogPostPath } from "@/data/blog-posts/path";
import { useLocalizedPath } from "@/i18n/useLocalizedPath";
import { fadeUp, stagger, viewportOnce } from "./motion-presets";

export const BlogTeaser = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("landing");
  const lp = useLocalizedPath();
  // Generated 3-newest-posts module — importing the full manifest here would
  // grow the landing entry chunk with every published article.
  const posts = blogTeaserPosts;

  return (
    <section className="relative py-24 px-4">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={stagger}
        className="container mx-auto max-w-6xl"
      >
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <motion.span
              variants={fadeUp}
              className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
            >
              {t("blogTeaser.eyebrow")}
            </motion.span>
            <motion.h2
              variants={fadeUp}
              className="mt-3 font-display text-3xl md:text-4xl font-semibold tracking-tight text-foreground"
            >
              {t("blogTeaser.title")}
            </motion.h2>
          </div>
          <motion.div variants={fadeUp}>
            <Button
              variant="ghost"
              onClick={() => navigate(lp("/blog"))}
              className="gap-2 text-muted-foreground hover:text-foreground"
            >
              {t("blogTeaser.viewAll")} <ArrowRight className="h-4 w-4" />
            </Button>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {posts.map((post) => (
            <motion.button
              key={post.id}
              variants={fadeUp}
              onClick={() => navigate(blogPostPath(post))}
              className="group text-left rounded-xl border border-border bg-card/30 hover:bg-card/60 p-6 transition-colors"
            >
              <Badge variant="secondary" className="mb-4 text-xs">
                {post.category}
              </Badge>
              <h3 className="font-display text-base font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2 leading-snug">
                {post.title}
              </h3>
              <p className="mt-2 text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                {post.description}
              </p>
              <div className="mt-5 flex items-center justify-between text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" /> {post.readTime}
                </span>
                <span>{post.date}</span>
              </div>
            </motion.button>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
